import React, { useState } from "react";

// A number box for the calculator. Shows the value formatted (250,000); while
// it is being edited it shows exactly what was typed, and tidies up on blur.
// Every keystroke reports the parsed value: blank, or anything that isn't a
// number of 0 or more, reports null (the calculator lists it as still needed).
interface Props {
  value: number | null;
  onChange: (value: number | null) => void;
  label: string;
  prefix?: string;
  suffix?: string;
  /** Decimals shown when not editing: [min, max]. */
  decimals?: [number, number];
  /** Grey italic: the value is calculated, not typed (a share before it is set by hand). */
  calculated?: boolean;
  /** Amber while blank: the offer can't be finished without it. */
  required?: boolean;
  placeholder?: string;
}

export function parseNumber(text: string): number | null {
  const t = text.replace(/[\s,$%×]/g, "");
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export default function NumberField({ value, onChange, label, prefix, suffix, decimals = [0, 2], calculated, required, placeholder }: Props) {
  const [editing, setEditing] = useState<string | null>(null);
  const [min, max] = decimals;
  const shown =
    editing ??
    (value === null ? "" : value.toLocaleString("en-US", { minimumFractionDigits: min, maximumFractionDigits: max }));
  const invalid = editing !== null && editing.trim() !== "" && parseNumber(editing) === null;
  const missing = required && value === null && !invalid;
  const cls = ["amb-num", invalid && "amb-num-invalid", missing && "amb-num-missing", calculated && "amb-num-calc"];
  return (
    <span className={cls.filter(Boolean).join(" ")}>
      {prefix && <span className="amb-num-affix">{prefix}</span>}
      <input
        inputMode="decimal"
        autoComplete="off"
        aria-label={label}
        aria-invalid={invalid || undefined}
        placeholder={placeholder}
        value={shown}
        // Keep exactly the text on screen: changing it here would drop the
        // selection a click or Tab just made, and typing would add to the old
        // number instead of replacing it. Commas are fine — parseNumber drops them.
        onFocus={() => setEditing(shown)}
        onChange={(e) => {
          setEditing(e.target.value);
          onChange(parseNumber(e.target.value));
        }}
        onBlur={() => setEditing(null)}
      />
      {suffix && <span className="amb-num-affix">{suffix}</span>}
    </span>
  );
}
